/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable column resize handle.
 *
 * Sits on a header cell's right edge, revealed on hover. Follows the ARIA
 * window-splitter pattern so the column can also be resized with the keyboard —
 * a drag-only affordance would be unreachable without a mouse.
 */
import cx from 'classnames';
import * as React from 'react';

/** Width change per arrow-key press. */
const KEYBOARD_STEP = 16;

/**
 * Upper bound for a drag, so a column cannot be widened so far that the rest of
 * the table is pushed out of reach.
 */
const MAX_COLUMN_WIDTH = 1200;

interface DataTableResizeHandleProps {
  columnKey: string;
  /** Accessible name, normally the column's header text. */
  label: string;
  minWidth: number;
  /**
   * Declared width, for `aria-valuenow`. Deliberately a plain number rather than
   * a measured value: reading layout during render forces a synchronous reflow of
   * the whole table, once per column, on every single render.
   */
  width?: number;
  /**
   * Width to start a drag from. Called on interaction only, never during render,
   * so it is free to measure the DOM.
   */
  getCurrentWidth: () => number;
  onPreview: (columnKey: string, width: number) => void;
  onCommit: (columnKey: string, width: number) => void;
  onResizeStart: (columnKey: string, startWidth: number) => void;
  onResizeEnd: () => void;
  isResizing: boolean;
  /** Builds the accessible name, so callers can translate it. */
  formatLabel: (columnLabel: string) => string;
}

const DataTableResizeHandle = ({
  columnKey,
  label,
  minWidth,
  width,
  getCurrentWidth,
  onPreview,
  onCommit,
  onResizeStart,
  onResizeEnd,
  isResizing,
  formatLabel,
}: DataTableResizeHandleProps) => {
  const clamp = (candidate: number) => Math.min(MAX_COLUMN_WIDTH, Math.max(minWidth, candidate));

  /**
   * Tears down an in-flight drag. Held in a ref so unmounting mid-drag can run it
   * — the listeners live on `window`, so without this they would outlive the table
   * and commit a width to a tree that no longer exists.
   */
  const dragCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(
    () => () => {
      dragCleanupRef.current?.();
    },
    []
  );

  /**
   * Pending keyboard width. Arrow keys preview like a drag and commit once on
   * release, so holding a key does not write to storage on every repeat.
   */
  const keyboardWidthRef = React.useRef<number | null>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Left button only, and never let the click reach the sort button behind it.
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = getCurrentWidth();

    onResizeStart(columnKey, startWidth);

    const handleMove = (moveEvent: PointerEvent) => {
      onPreview(columnKey, clamp(startWidth + (moveEvent.clientX - startX)));
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      dragCleanupRef.current = null;
      onResizeEnd();
    };

    function handleUp(upEvent: PointerEvent) {
      cleanup();
      onCommit(columnKey, clamp(startWidth + (upEvent.clientX - startX)));
    }

    dragCleanupRef.current = cleanup;

    // Listeners go on the window so the drag survives the pointer leaving the
    // handle, which it always does.
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  };

  const commitKeyboardWidth = () => {
    if (keyboardWidthRef.current === null) return;

    const pending = keyboardWidthRef.current;
    keyboardWidthRef.current = null;
    onResizeEnd();
    onCommit(columnKey, pending);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowLeft' ? -KEYBOARD_STEP : event.key === 'ArrowRight' ? KEYBOARD_STEP : 0;
    if (!step) return;

    event.preventDefault();
    event.stopPropagation();

    const from = keyboardWidthRef.current ?? getCurrentWidth();
    if (keyboardWidthRef.current === null) {
      onResizeStart(columnKey, from);
    }

    const next = clamp(from + step);
    keyboardWidthRef.current = next;
    onPreview(columnKey, next);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={formatLabel(label)}
      aria-valuemin={minWidth}
      aria-valuemax={MAX_COLUMN_WIDTH}
      // A focusable separator must report a current value. Flexible columns have
      // no declared width, so fall back to the lower bound rather than omitting it.
      aria-valuenow={width ?? minWidth}
      tabIndex={0}
      className={cx('ds-data-table__resize-handle', {
        'ds-data-table__resize-handle--active': isResizing,
      })}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onKeyUp={commitKeyboardWidth}
      onBlur={commitKeyboardWidth}
    />
  );
};

DataTableResizeHandle.displayName = 'DataTableResizeHandle';

export default DataTableResizeHandle;
